import React from 'react';
import { useField } from 'formik';
import { CharCount, FieldWrapper, LimitWarning } from './styles';

interface LimitedFieldProps {
  id?: string;
  name: string;
  label?: React.ReactNode;
  required?: boolean;
  type?: string;
  maxLength?: number;
  warningThreshold?: number;
  placeholder?: string;
  className?: string;
}

/**
 * Campo de texto com contador de caracteres e aviso progressivo quando
 * se aproxima do limite máximo.
 *
 * - warningThreshold (default 10): exibe o contador em amarelo quando restam
 *   `warningThreshold` ou menos caracteres.
 * - Ao atingir o limite, o contador fica vermelho e exibe mensagem de erro.
 */
const LimitedField: React.FC<LimitedFieldProps> = ({
  id,
  name,
  label,
  required,
  type = 'text',
  maxLength = 255,
  warningThreshold = 10,
  placeholder,
  className,
}) => {
  const [field] = useField(name);
  const length = field.value ? String(field.value).length : 0;
  const remaining = maxLength - length;

  const isExceeded = length >= maxLength;
  const isWarning = !isExceeded && remaining <= warningThreshold;

  const countState: 'exceeded' | 'warning' | 'normal' = isExceeded ? 'exceeded' : isWarning ? 'warning' : 'normal';

  return (
    <FieldWrapper>
      {label && (
        <label htmlFor={id || name}>
          {label}
          {required && <span>*</span>}
        </label>
      )}
      <input
        {...field}
        id={id || name}
        type={type}
        maxLength={maxLength}
        placeholder={placeholder}
        className={className}
      />
      {isWarning && !isExceeded && (
        <LimitWarning>
          Faltam <strong>{remaining}</strong> caractere{remaining !== 1 ? 's' : ''}
        </LimitWarning>
      )}
      <CharCount state={countState}>
        {length}/{maxLength}
        {isExceeded && ' — limite de caracteres atingido'}
      </CharCount>
    </FieldWrapper>
  );
};

export default LimitedField;
